import { useState, type ReactNode } from "react";
import { Eye, Search, X } from "lucide-react";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { Pager, paginate } from "../Pager";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import {
  applyRegister,
  categoryOptions,
  countRegisterFilters,
  initialRegisterState,
  type RegisterConfig,
  type RegisterState,
} from "./registerLogic";

export interface RegisterColumn<T> {
  header: string;
  cell: (row: T) => ReactNode;
  /** The value written to CSV/Excel/PDF; columns without one are left out of exports. */
  exportValue?: (row: T) => string | number;
  align?: "left" | "right";
  className?: string;
  /** Written to exports but not shown, e.g. a fact the views above already split on. */
  exportOnly?: boolean;
}

const PAGE_SIZES = [10, 25, 50];

/**
 * A register card for rows that all arrive at once: a view switch with
 * counts, search, a date range and sort, a spreadsheet table whose rows open
 * the record, a pager and CSV/Excel/PDF export of what the filters show.
 */
export function ClientRegister<T>({
  title,
  description,
  actions,
  rows,
  config,
  columns,
  rowKey,
  onOpen,
  openLabel,
  noun,
  exportName,
  isLoading,
  isError,
  onRetry,
  emptyText,
  canExport = true,
  initialState,
}: {
  title: string;
  description?: string;
  /** Buttons beside the export, e.g. "New charge". */
  actions?: ReactNode;
  rows: T[] | undefined;
  config: RegisterConfig<T>;
  columns: RegisterColumn<T>[];
  rowKey: (row: T) => string | number;
  /** Left out for a read-only table: no row click and no action column. */
  onOpen?: (row: T) => void;
  /** The row button's word, e.g. "Record payment" when something is owed. */
  openLabel?: (row: T) => string;
  /** [singular, plural], e.g. ["charge", "charges"]. */
  noun: [string, string];
  /** File name stem and document title for exports. */
  exportName: { file: string; title: string };
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyText: string;
  /** False hides the export buttons from people who may not download reports. */
  canExport?: boolean;
  /** Filters to open with, e.g. one class; remount (key) to apply new ones. */
  initialState?: Partial<RegisterState>;
}) {
  const [state, setState] = useState<RegisterState>(() => ({ ...initialRegisterState(config), ...initialState }));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const update = (changes: Partial<RegisterState>) => {
    setState((current) => ({ ...current, ...changes }));
    setPage(1);
  };

  const { rows: shown, counts } = applyRegister(rows ?? [], state, config);
  const pageRows = paginate(shown, page, pageSize);
  const activeFilters = countRegisterFilters(state);
  const categories = categoryOptions(rows ?? [], config);
  const filterCount = 2 + (config.date ? 2 : 0) + (config.category ? 1 : 0);
  const exported = columns.filter((column) => column.exportValue);
  const shownColumns = columns.filter((column) => !column.exportOnly);
  const exportRows = (format: ExportFormat) => {
    const exportColumns = exported.map((column) => ({ header: column.header, accessor: column.exportValue! }));
    if (format === "csv") exportAsCsv(`${exportName.file}.csv`, exportColumns, shown);
    if (format === "excel") exportAsExcel(`${exportName.file}.xls`, exportName.title, exportColumns, shown);
    if (format === "pdf") exportAsPdf(exportName.title, exportColumns, shown);
  };

  return (
    <div className="card">
      <div className="card-header pb-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canExport && <ExportButtons onExport={exportRows} disabled={!shown.length} what={`the filtered ${noun[1]}`} />}
            {actions}
          </div>
        </div>
      </div>
      <div className="card-content space-y-4">
        <div className="filter-panel">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {config.views.length > 1 && (
              <div role="group" aria-label={`${title} view`} className="segmented">
                {config.views.map((view) => (
                  <button
                    key={view.key}
                    type="button"
                    aria-pressed={state.view === view.key}
                    onClick={() => update({ view: view.key })}
                    className="segmented-option"
                  >
                    {view.label}
                    <span className="ml-1.5 tabular-nums opacity-70">{counts[view.key]}</span>
                  </button>
                ))}
              </div>
            )}
            {activeFilters > 0 && (
              <button
                type="button"
                onClick={() => update({ search: "", dateFrom: "", dateTo: "", category: "" })}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
              >
                <X className="h-3.5 w-3.5" /> Clear {activeFilters} {activeFilters === 1 ? "filter" : "filters"}
              </button>
            )}
          </div>
          <div className={`grid gap-3 sm:grid-cols-2 ${filterCount >= 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
            <label className="filter-field sm:col-span-2 lg:col-span-1">
              Search
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="search"
                  value={state.search}
                  onChange={(event) => update({ search: event.target.value })}
                  className="input-compact pl-8"
                  placeholder="Search"
                />
              </div>
            </label>
            {config.date && (
              <>
                <label className="filter-field">
                  {config.date.label} from
                  <input type="date" value={state.dateFrom} max={state.dateTo || undefined} onChange={(event) => update({ dateFrom: event.target.value })} className="input-compact" />
                </label>
                <label className="filter-field">
                  {config.date.label} to
                  <input type="date" value={state.dateTo} min={state.dateFrom || undefined} onChange={(event) => update({ dateTo: event.target.value })} className="input-compact" />
                </label>
              </>
            )}
            {config.category && (
              <label className="filter-field">
                {config.category.label}
                <select value={state.category} onChange={(event) => update({ category: event.target.value })} className="input-compact">
                  <option value="">All</option>
                  {categories.map((value) => <option key={value} value={value}>{value}</option>)}
                </select>
              </label>
            )}
            <label className="filter-field">
              Sort by
              <select value={state.sort} onChange={(event) => update({ sort: event.target.value })} className="input-compact">
                {config.sorts.map((sort) => <option key={sort.key} value={sort.key}>{sort.label}</option>)}
              </select>
            </label>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, index) => <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />)}
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            The {noun[1]} could not be loaded.{" "}
            {onRetry && <button type="button" className="font-medium underline" onClick={onRetry}>Try again</button>}
          </div>
        ) : !shown.length ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
            {rows?.length ? `No ${noun[1]} match these filters.` : emptyText}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="sheet-wrap">
              <table className="sheet">
                <thead>
                  <tr>
                    <th className="sheet-rownum">#</th>
                    {shownColumns.map((column) => (
                      <th key={column.header} className={column.align === "right" ? "text-right" : undefined}>{column.header}</th>
                    ))}
                    {onOpen && <th className="text-right">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.rows.map((row, index) => (
                    <tr key={rowKey(row)} className={onOpen ? "cursor-pointer" : undefined} onClick={onOpen && (() => onOpen(row))}>
                      <td className="sheet-rownum">{pageRows.first + index}</td>
                      {shownColumns.map((column) => (
                        <td key={column.header} className={`${column.align === "right" ? "text-right tabular-nums" : ""} ${column.className ?? ""}`}>
                          {column.cell(row)}
                        </td>
                      ))}
                      {onOpen && (
                        <td className="text-right">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpen(row);
                            }}
                            className="inline-flex items-center gap-1 whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                          >
                            <Eye className="h-3.5 w-3.5" /> {openLabel?.(row) ?? "Open"}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              first={pageRows.first}
              shown={pageRows.rows.length}
              total={shown.length}
              noun={shown.length === 1 ? noun[0] : noun[1]}
              page={pageRows.page}
              pageCount={pageRows.pageCount}
              onPage={setPage}
              pageSize={pageSize}
              pageSizes={PAGE_SIZES}
              onPageSize={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** A status with its coloured dot, as the registers show it. */
export function StatusDot({ label, dot }: { label: string; dot: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
